import type {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
  ResponseType,
} from 'axios';
import axios from 'axios';
import dayjs from 'dayjs';
import { refreshAccessToken } from '@/shared/api/refreshToken';
import { isApiControllerAuthorized } from '@/shared/lib/routePermissions';
import { sessionAtom } from '@/shared/store/atoms/sessionAtom';
import { jotaiStore } from '@/shared/store/store';

// ─── Tipos exigidos pelo Kubb ─────────────────────────────────────────────────

/** Configuração de requisição usada pelos clientes gerados pelo Kubb. */
export interface RequestConfig<TData = unknown> {
  baseURL?: string | undefined;
  url?: string | undefined;
  method: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE';
  params?: unknown;
  data?: TData | FormData | undefined;
  responseType?: ResponseType | undefined;
  signal?: AbortSignal | undefined;
  headers?: Record<string, string> | undefined;
  /** Callback de progresso do upload — usado por chamadas multipart que reportam bytes enviados. */
  onUploadProgress?: ((event: AxiosProgressEvent) => void) | undefined;
}

/** Configuração de resposta usada pelos clientes gerados pelo Kubb. */
export interface ResponseConfig<TData = unknown> {
  data: TData;
  status: number;
  statusText: string;
  headers?: AxiosResponse['headers'];
}

/** Tipo de erro de resposta usado pelos hooks gerados pelo Kubb. */
export type ResponseErrorConfig<TError = unknown> = AxiosError<TError>;

/**
 * Tipo da função cliente HTTP — usado pelo Kubb para permitir injeção de cliente customizado.
 * Deve ser compatível com a assinatura de `axiosClient`.
 */
export type Client = <TData, _TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
) => Promise<ResponseConfig<TData>>;

// ─── Instância Axios ──────────────────────────────────────────────────────────

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    PartnerAuthorization: import.meta.env.VITE_PARTNER_AUTH,
  },
  // Axios 1.x serializa arrays como "param[]=1&param[]=2" por padrão.
  // ASP.NET Core espera "param=1&param=2" (repeated params sem brackets).
  paramsSerializer: { indexes: null },
});

// ─── Refresh token state ─────────────────────────────────────────────────────

let isRefreshing = false;
let pendingQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

function flushQueue(err: unknown, token: string | null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (err) reject(err);
    else if (token) resolve(token);
  });
  pendingQueue = [];
}

async function performTokenRefresh(accessToken: string): Promise<string> {
  isRefreshing = true;
  try {
    const { token: newToken, tokenExpireDate } = await refreshAccessToken(accessToken);
    const session = jotaiStore.get(sessionAtom);
    if (!session) throw new Error('refresh_failed');
    jotaiStore.set(sessionAtom, { ...session, accessToken: newToken, tokenExpireDate });
    flushQueue(null, newToken);
    return newToken;
  } catch (err) {
    flushQueue(err, null);
    jotaiStore.set(sessionAtom, null);
    window.location.href = '/login';
    throw err;
  } finally {
    isRefreshing = false;
  }
}

// ─── Request interceptor ─────────────────────────────────────────────────────

instance.interceptors.request.use(async config => {
  // O Kubb gera Content-Type: application/*+json, mas o ASP.NET Core rejeita esse
  // media type com 415 Unsupported Media Type. Normaliza para application/json.
  if (config.headers['Content-Type'] === 'application/*+json') {
    config.headers['Content-Type'] = 'application/json';
  }

  const session = jotaiStore.get(sessionAtom);

  if (!session?.accessToken) return config;

  // Bloqueia chamadas para controllers que o usuário não tem permissão
  // Master tem acesso irrestrito
  const url = config.url ?? '';
  if (
    !session.roles.includes('Master') &&
    !isApiControllerAuthorized(url, session.controllerPermissions)
  ) {
    return Promise.reject(
      Object.assign(new Error(`Acesso negado ao controller: ${url}`), {
        code: 'CONTROLLER_UNAUTHORIZED',
      }),
    );
  }

  const isTokenExpired = !dayjs().isBefore(dayjs(session.tokenExpireDate).subtract(5, 'minute'));

  if (isTokenExpired) {
    if (isRefreshing) {
      const newToken = await new Promise<string>((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      });
      config.headers.Authorization = `Bearer ${newToken}`;
      return config;
    }

    const newToken = await performTokenRefresh(session.accessToken);
    config.headers.Authorization = `Bearer ${newToken}`;
    return config;
  }

  config.headers.Authorization = `Bearer ${session.accessToken}`;
  return config;
});

// ─── Response interceptor ────────────────────────────────────────────────────

instance.interceptors.response.use(
  response => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: token => {
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `Bearer ${token}`,
              };
              resolve(instance(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      const session = jotaiStore.get(sessionAtom);
      const newToken = await performTokenRefresh(session?.accessToken ?? '');
      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${newToken}`,
      };
      return instance(originalRequest);
    }

    // 403 e 5xx são tratados via toast — a integração ocorre no AppProviders
    // após o toast provider ser inicializado (ver docs/conventions.md).

    return Promise.reject(error);
  },
);

// ─── Cliente HTTP (contrato exigido pelo Kubb) ────────────────────────────────

/**
 * Função cliente HTTP usada pelos arquivos gerados pelo Kubb.
 *
 * Recebe a configuração de requisição e retorna a resposta tipada.
 * O Kubb importa esta função como `default` de `@/shared/api/axiosInstance`.
 *
 * Para uso direto fora do Kubb, importe `axiosInstance`.
 */
export async function axiosClient<TData, _TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> {
  const response = await instance.request<TData>(config as AxiosRequestConfig<TVariables>);
  return response as unknown as ResponseConfig<TData>;
}

/**
 * Instância Axios configurada com interceptors de autenticação e erro.
 *
 * Use esta instância para chamadas HTTP manuais fora do código gerado pelo Kubb.
 * Nunca instancie o Axios diretamente.
 */
export const axiosInstance = instance;

export default axiosClient;
